/**
 * Central Listing Lifecycle & Expiry Configuration
 * Cameroon Land Market
 */

export const LISTING_CONFIG = {
  // Configurable listing expiration duration in days (server-authoritative)
  DEFAULT_EXPIRY_DAYS: 60,
  // Retention grace period in days after expiry/sold before permanent 410 Gone removal
  EXPIRED_RETENTION_DAYS: 90,
  // Specific timeframe in days before expiration when renewal becomes eligible
  RENEWAL_THRESHOLD_DAYS: 7,
} as const;

export const DEFAULT_EXPIRY_DAYS = LISTING_CONFIG.DEFAULT_EXPIRY_DAYS;
export const EXPIRED_RETENTION_DAYS = LISTING_CONFIG.EXPIRED_RETENTION_DAYS;
export const EXPIRATION_GRACE_PERIOD_DAYS = LISTING_CONFIG.EXPIRED_RETENTION_DAYS;
export const RENEWAL_THRESHOLD_DAYS = LISTING_CONFIG.RENEWAL_THRESHOLD_DAYS;

/**
 * Calculates a server-authoritative expiry Date based on a starting timestamp
 */
export function calculateExpiryDate(fromDate: Date = new Date(), durationDays: number = LISTING_CONFIG.DEFAULT_EXPIRY_DAYS): Date {
  const result = new Date(fromDate.getTime());
  result.setDate(result.getDate() + durationDays);
  return result;
}

/**
 * Returns number of days remaining until expiration.
 * Positive number: days remaining
 * 0 or negative: expired
 */
export function getDaysRemaining(expiresAt?: string | Date | null): number {
  if (!expiresAt) return 0;
  const expiry = typeof expiresAt === 'string' ? new Date(expiresAt) : expiresAt;
  if (isNaN(expiry.getTime())) return 0;
  const now = new Date();
  const diffMs = expiry.getTime() - now.getTime();
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return Math.max(0, days);
}

/**
 * Checks if a listing has reached its expiry threshold
 */
export function isListingExpired(expiresAt?: string | Date | null): boolean {
  if (!expiresAt) return false;
  const expiry = typeof expiresAt === 'string' ? new Date(expiresAt) : expiresAt;
  if (isNaN(expiry.getTime())) return false;
  return Date.now() >= expiry.getTime();
}

/**
 * Checks if an expired listing has exceeded its retention window (for 410 Gone)
 */
export function isPastRetentionPeriod(expiresAt?: string | Date | null, retentionDays = LISTING_CONFIG.EXPIRED_RETENTION_DAYS): boolean {
  if (!expiresAt) return false;
  const expiry = typeof expiresAt === 'string' ? new Date(expiresAt) : expiresAt;
  if (isNaN(expiry.getTime())) return false;
  const retentionDeadline = new Date(expiry.getTime());
  retentionDeadline.setDate(retentionDeadline.getDate() + retentionDays);
  return Date.now() >= retentionDeadline.getTime();
}

/**
 * Checks if a listing is eligible for renewal.
 * A listing is eligible for renewal ONLY if:
 * 1. It has expired (status === 'expired' or expiresAt timestamp is in the past), OR
 * 2. It is within the specific timeframe of expiration (default: <= 7 days remaining).
 * When a listing is currently active and outside this timeframe (> 7 days), this returns false.
 */
export function isEligibleForRenewal(
  expiresAt?: string | Date | null,
  status?: string,
  thresholdDays: number = LISTING_CONFIG.RENEWAL_THRESHOLD_DAYS
): boolean {
  const normStatus = (status || '').toLowerCase().trim();
  if (normStatus === 'draft' || normStatus === 'pending' || normStatus === 'sold' || normStatus === 'rented') {
    return false;
  }
  if (normStatus === 'expired') return true;
  if (isListingExpired(expiresAt)) return true;
  if (!expiresAt) return false;
  const days = getDaysRemaining(expiresAt);
  return days <= thresholdDays;
}

/**
 * Localized human-readable formatting of expiry date and remaining days
 */
export function formatExpiryInfo(expiresAt?: string | Date | null, langOrIsFr: boolean | string = false): {
  isExpired: boolean;
  daysRemaining: number;
  formattedDate: string;
  badgeText: string;
  statusLabel: string;
  daysRemainingLabel: string;
  renewButtonLabel: string;
  canRenew: boolean;
} {
  const isFr = typeof langOrIsFr === 'string' ? langOrIsFr.toLowerCase().startsWith('fr') : Boolean(langOrIsFr);
  const days = getDaysRemaining(expiresAt);
  const isExp = isListingExpired(expiresAt);
  const canRenew = isExp || days <= LISTING_CONFIG.RENEWAL_THRESHOLD_DAYS;

  let formattedDate = '';
  if (expiresAt) {
    const d = typeof expiresAt === 'string' ? new Date(expiresAt) : expiresAt;
    if (!isNaN(d.getTime())) {
      formattedDate = d.toLocaleDateString(isFr ? 'fr-FR' : 'en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    }
  }

  let badgeText = '';
  let daysRemainingLabel = '';
  if (isExp) {
    badgeText = isFr ? `Expiré le ${formattedDate}` : `Expired on ${formattedDate}`;
    daysRemainingLabel = isFr ? 'Expirée' : 'Expired';
  } else if (days === 0) {
    badgeText = isFr ? "Expire aujourd'hui" : 'Expires today';
    daysRemainingLabel = isFr ? "Expire aujourd'hui" : 'Expires today';
  } else if (days === 1) {
    badgeText = isFr ? 'Expire demain' : 'Expires tomorrow';
    daysRemainingLabel = isFr ? '1 jour restant' : '1 day remaining';
  } else {
    badgeText = isFr ? `Expire dans ${days} jours` : `Expires in ${days} days`;
    daysRemainingLabel = isFr ? `${days} jours restants` : `${days} days remaining`;
  }

  const statusLabel = isExp
    ? (isFr ? 'Expiré' : 'Expired')
    : (isFr ? 'Actif' : 'Active');

  const renewButtonLabel = isFr ? 'Renouveler l’annonce' : 'Renew listing';

  return {
    isExpired: isExp,
    daysRemaining: days,
    formattedDate,
    badgeText,
    statusLabel,
    daysRemainingLabel,
    renewButtonLabel,
    canRenew,
  };
}

/**
 * Canonical Listing Status classification.
 * Strictly partitions listings into mutually exclusive states:
 * - 'draft': Listing draft created/saved, not yet published
 * - 'pending': Listing submitted and awaiting administrative/cadastral review
 * - 'sold': Property marked as sold
 * - 'rented': Property marked as rented
 * - 'expired': Listing whose active duration has elapsed or status is explicitly 'expired'
 * - 'active': Listing currently live and publicly visible
 */
export type CanonicalListingStatus = 'draft' | 'pending' | 'sold' | 'rented' | 'expired' | 'active';

export function getCanonicalListingStatus(property: {
  status?: string | null;
  expiresAt?: string | Date | null;
  expires_at?: string | Date | null;
}): CanonicalListingStatus {
  const rawStatus = (property.status || '').toLowerCase().trim();

  // 1. Explicit non-active lifecycle states take precedence
  if (rawStatus === 'draft') return 'draft';
  if (rawStatus === 'pending') return 'pending';
  if (rawStatus === 'sold') return 'sold';
  if (rawStatus === 'rented') return 'rented';
  if (rawStatus === 'expired') return 'expired';

  // 2. Active listings checked against expiration threshold
  const expiry = property.expiresAt || property.expires_at;
  if (isListingExpired(expiry)) {
    return 'expired';
  }

  return 'active';
}

/**
 * Filter predicate to match a listing against a selected tab:
 * - 'all': matches all listings
 * - 'active': only live active listings
 * - 'expired': only expired listings
 * - 'draft': only drafts
 * - 'pending': only under review listings
 * - 'sold': matches both 'sold' and 'rented' listings
 * - 'rented': matches 'rented' listings
 */
export function matchesListingStatusFilter(
  property: { status?: string | null; expiresAt?: string | Date | null; expires_at?: string | Date | null },
  filter: 'all' | 'active' | 'draft' | 'pending' | 'expired' | 'sold' | 'rented'
): boolean {
  if (filter === 'all') return true;
  const canonical = getCanonicalListingStatus(property);
  if (filter === 'sold') {
    return canonical === 'sold' || canonical === 'rented';
  }
  return canonical === filter;
}

/**
 * Calculates mutually exclusive counts for each status tab from an array of listings.
 * Guarantees: active + expired + draft + pending + sold = total.
 */
export function calculateListingStatusCounts<T extends { status?: string | null; expiresAt?: string | Date | null; expires_at?: string | Date | null }>(
  properties: T[]
): {
  total: number;
  active: number;
  expired: number;
  draft: number;
  pending: number;
  sold: number;
} {
  let active = 0;
  let expired = 0;
  let draft = 0;
  let pending = 0;
  let sold = 0;

  for (const prop of properties) {
    const status = getCanonicalListingStatus(prop);
    switch (status) {
      case 'active':
        active++;
        break;
      case 'expired':
        expired++;
        break;
      case 'draft':
        draft++;
        break;
      case 'pending':
        pending++;
        break;
      case 'sold':
      case 'rented':
        sold++;
        break;
    }
  }

  return {
    total: properties.length,
    active,
    expired,
    draft,
    pending,
    sold,
  };
}
